import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useAuth } from '@/app/AuthProvider'
import { fetchAppUpdates, markAppUpdatesRead } from './api'
import type { AppUpdateItem } from './types'

interface UpdatesContextValue {
  open: boolean
  setOpen: (v: boolean) => void
  items: AppUpdateItem[]
  unread: number
  /** IDs that were unread when the modal was opened (for «Новое» badges). */
  highlightIds: Set<number>
  lastSeenUpdateId: number
  loading: boolean
  selectedId: number | null
  setSelectedId: (id: number | null) => void
  markRead: () => Promise<void>
  refresh: () => Promise<void>
}

const UpdatesContext = createContext<UpdatesContextValue | null>(null)

export function UpdatesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [open, setOpenState] = useState(false)
  const [items, setItems] = useState<AppUpdateItem[]>([])
  const [unread, setUnread] = useState(0)
  const [lastSeenUpdateId, setLastSeenUpdateId] = useState(0)
  const [highlightIds, setHighlightIds] = useState<Set<number>>(() => new Set())
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const markingRef = useRef(false)

  const refresh = useCallback(async () => {
    if (!user?.id) {
      setItems([])
      setUnread(0)
      setLastSeenUpdateId(0)
      return
    }
    setLoading(true)
    try {
      const data = await fetchAppUpdates()
      const nextItems = data.items || []
      setItems(nextItems)
      setUnread(Number(data.unreadCount) || 0)
      setLastSeenUpdateId(Number(data.lastSeenUpdateId) || 0)
      setSelectedId((prev) => {
        if (prev && nextItems.some((i) => i.id === prev)) return prev
        return nextItems[0]?.id ?? null
      })
    } catch {
      /* silent: badge is non-critical */
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const markRead = useCallback(async () => {
    if (markingRef.current) return
    markingRef.current = true
    const latestId = items[0]?.id
    try {
      const data = await markAppUpdatesRead(latestId)
      setUnread(Number(data.unreadCount) || 0)
      setLastSeenUpdateId(Number(data.lastSeenUpdateId) || latestId || 0)
    } catch {
      setUnread(0)
    } finally {
      markingRef.current = false
    }
  }, [items])

  const setOpen = useCallback(
    (v: boolean) => {
      if (v) {
        const snapshot = new Set(
          items.filter((item) => item.id > lastSeenUpdateId).map((item) => item.id),
        )
        setHighlightIds(snapshot)
        if (!selectedId && items[0]) setSelectedId(items[0].id)
        setOpenState(true)
        if (snapshot.size > 0) void markRead()
      } else {
        setOpenState(false)
      }
    },
    [items, lastSeenUpdateId, markRead, selectedId],
  )

  const value = useMemo(
    () => ({
      open,
      setOpen,
      items,
      unread,
      highlightIds,
      lastSeenUpdateId,
      loading,
      selectedId,
      setSelectedId,
      markRead,
      refresh,
    }),
    [
      open,
      setOpen,
      items,
      unread,
      highlightIds,
      lastSeenUpdateId,
      loading,
      selectedId,
      markRead,
      refresh,
    ],
  )

  return <UpdatesContext.Provider value={value}>{children}</UpdatesContext.Provider>
}

export function useUpdates() {
  const ctx = useContext(UpdatesContext)
  if (!ctx) throw new Error('useUpdates must be used within UpdatesProvider')
  return ctx
}
